// Libs
import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import invariant from 'tiny-invariant';

// RDS
import Button from '@ramsey-design-system/button';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import Select from '@ramsey-design-system/select';
import Text from '@ramsey-design-system/text';

// Components
import Dialog from '~fpu/shared/components/Dialog';

// Hooks
import { useCancelWorkshop } from '../hooks/useCancelWorkshop';
import { useMixpanelEvent } from '~fpu/shared/hooks/useMixpanelEvent';
import { STEPS, useWorkshopStepContext } from './WorkshopStepContext';
import { useUserQuery } from '~fpu/shared/global-queries';
import { useWorkshopRegistrants } from '../hooks/useWorkshopRegistrants';

// Types
import { Workshop } from '../types';

// Utils
import { PATHS } from '~fpu/config/constants';
import {
  formatWorkshopType,
  parseWorkshopDateTime
} from '~fpu/shared/utils/workshop-utils';

function CancelWorkshop() {
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const { isEditMode, currentStep } = useWorkshopStepContext();

  if (!isEditMode || currentStep !== STEPS.SUMMARY) {
    return null;
  }

  const handleClick = () => {
    setShowRescheduleDialog(true);
  };

  return (
    <>
      <section className="gap-lg mt-5 flex flex-col justify-between rounded-xl bg-white p-3.5 shadow-md sm:flex-row sm:items-center">
        <Text>Don&apos;t want to host this workshop anymore?</Text>
        <Button appearance="danger" onClick={handleClick} fullWidthOnMobile>
          Cancel Workshop
        </Button>
      </section>

      <RescheduleWorkshopDialog
        isOpen={showRescheduleDialog}
        setIsOpen={setShowRescheduleDialog}
        setShowConfirmationDialog={setShowConfirmationDialog}
      />

      <CancelWorkshopConfirmation
        isOpen={showConfirmationDialog}
        setIsOpen={setShowConfirmationDialog}
      />
    </>
  );
}

function RescheduleWorkshopDialog({
  isOpen,
  setIsOpen,
  setShowConfirmationDialog
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setShowConfirmationDialog: Dispatch<SetStateAction<boolean>>;
}) {
  const { helpers } = useWorkshopStepContext();

  const handleContinue = () => {
    setIsOpen(false);
    setShowConfirmationDialog(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleEditWorkshop = () => {
    setIsOpen(false);
    helpers.setStep(STEPS.SCHEDULE);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Dialog.Title level="2" className="mb-4">
        Would you like to reschedule your workshop?
      </Dialog.Title>
      <Text className="mb-4">
        Rescheduling gives people more time to join your workshop (and get help
        with their money). To change your workshop date, go back to the Schedule
        step.
      </Text>

      <div className="gap-md flex flex-col-reverse md:flex-row">
        <Button onClick={handleEditWorkshop} size="large" fullWidthOnMobile>
          Edit Workshop Date
        </Button>
        <Button
          onClick={handleContinue}
          appearance="dangerSubtle"
          size="large"
          fullWidthOnMobile
        >
          Continue to Cancel
        </Button>
      </div>
    </Dialog>
  );
}

function CancelWorkshopConfirmation({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const [confirmationText, setConfirmationText] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const { workshopId } = useParams() as { workshopId: string };
  invariant(workshopId);

  const { data: user } = useUserQuery();
  const { data: registrants } = useWorkshopRegistrants(workshopId);

  const navigate = useNavigate();
  const { mutate, isPending } = useCancelWorkshop(workshopId);
  const { mutate: trackMixpanelEvent } = useMixpanelEvent();

  const handleReasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCancelReason(event.target.value);
  };

  const onCancelSuccess = (canceledWorkshop: Workshop) => {
    const workshopDateTime = parseWorkshopDateTime(
      canceledWorkshop.workshopDate,
      canceledWorkshop.startTime
    );

    const workshopRegistrantCount = registrants?.length;

    trackMixpanelEvent({
      event: 'Workshop Canceled',
      properties: {
        workshop_id: canceledWorkshop.id,
        organization_id: canceledWorkshop.organizationId,
        canceled_by_user_id: user?.axid,
        type: formatWorkshopType(canceledWorkshop.type),
        cancel_reason: cancelReason,
        workshop_date: workshopDateTime.toISOString(),
        canceled_on_date: new Date().toISOString(),
        registrant_count: workshopRegistrantCount
      }
    });

    navigate(PATHS.FPU_DASHBOARD.BUDGET_WORKSHOP);

    toast.success('Your workshop has been canceled.');
  };

  const handleWorkshopCancellation = () => {
    mutate(undefined, {
      onSuccess: ({ data }) => onCancelSuccess(data)
    });
  };

  const handleClose = () => {
    setConfirmationText('');
    setCancelReason('');
    setIsOpen(false);
  };

  const OPTIONS = [
    '',
    'I made this workshop accidentally',
    'This workshop is a duplicate',
    'No one registered for the workshop',
    "Members didn't show for workshop",
    'I moved',
    'Event out of my control',
    'Other'
  ];

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Dialog.Title level="2" className="mb-4">
        Cancel Workshop
      </Dialog.Title>

      <FormField
        label="First, let us know why you want to cancel your workshop."
        className="mb-4 [&>label]:mb-2!"
      >
        {(props) => (
          <Select {...props} onChange={handleReasonChange} value={cancelReason}>
            {OPTIONS.map((option) => (
              <option
                key={option}
                value={option}
                data-testid={`${option}-selection`}
              >
                {option || 'Select a Reason'}
              </option>
            ))}
          </Select>
        )}
      </FormField>

      <FormField
        label='Confirm the cancellation by typing "CANCEL" in the box below:'
        length="sm"
        className="mb-6 [&>label]:mb-2!"
      >
        {(props) => (
          <Input
            {...props}
            type="text"
            data-testid="cancel-workshop-input"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={cancelReason === ''}
          />
        )}
      </FormField>

      <div className="gap-md flex flex-col-reverse md:flex-row">
        <Button
          onClick={handleWorkshopCancellation}
          appearance="danger"
          size="large"
          fullWidthOnMobile
          disabled={confirmationText !== 'CANCEL'}
          data-testid="cancel-workshop-button"
          loading={isPending}
        >
          Cancel My Workshop
        </Button>
        <Button
          appearance="subtle"
          size="large"
          fullWidthOnMobile
          data-testid="dont-cancel-workshop-button"
          onClick={handleClose}
        >
          Never Mind, Don&apos;t Cancel
        </Button>
      </div>
    </Dialog>
  );
}

export default CancelWorkshop;
